import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { fetchAdmin, postAdmin, toErrorMessage } from '../../admin/api';
import { PATH_DASHBOARD } from '../../routes/paths';
import { AdminRouteLayout } from './AdminRoutePageShared';
import { AdminFormSection } from '../../sections/admin/shared';
import {
  ALLOWED_FILE_TYPES,
  DIRECTION_OPTIONS,
  FILE_ACCEPT_ATTRIBUTE,
  TARGET_TYPE_OPTIONS,
  WALLET_TYPE_OPTIONS,
  buildCreatePayload,
  formatDecimalForDisplay,
  formatReasonCodeLabel,
  getAssetCodeForWalletType,
  isReasonCodeActive,
  normalizeDecimalInput,
  reasonCodeRequiresAttachment,
  reasonCodeRequiresReference,
} from '../../sections/admin/adjustments/adjustmentsShared';

const LEDGER_FORM_DEFAULTS = {
  target_type: 'user',
  user_id: '',
  wallet_type: 'fiat',
  asset_code: 'NGN',
  direction: 'credit',
  amount: '',
  reason_code: '',
  reason_note: '',
  reference_type: '',
  reference_id: '',
};

const SNAPSHOT_FORM_DEFAULTS = {
  target_type: 'user',
  user_id: '',
  direction: 'credit',
  balance: '',
  ledger_balance: '',
  usdt_balance: '',
  usdt_ledger_balance: '',
  reason_code: '',
  reason_note: '',
  reference_type: '',
  reference_id: '',
};

function sanitizeFiles(fileList) {
  return Array.from(fileList || []);
}

function FileSummary({ files, error }) {
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Attachments</Typography>
      <Typography variant="body2" color="text.secondary">
        Upload supporting files as `userfiles[]`. The backend only accepts PDF, JPG, PNG, WEBP, DOC, and DOCX files.
      </Typography>
      {files.length ? (
        <Stack spacing={0.75}>
          {files.map((file) => (
            <Typography key={`${file.name}-${file.size}`} variant="body2">
              {file.name} ({file.type || 'unknown type'})
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No files selected.
        </Typography>
      )}
      {error ? (
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      ) : null}
    </Stack>
  );
}

FileSummary.propTypes = {
  error: PropTypes.string,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      type: PropTypes.string,
    })
  ).isRequired,
};

function ReasonCodeHelper({ reasonCode }) {
  if (!reasonCode) {
    return (
      <Typography variant="body2" color="text.secondary">
        Choose an active backend reason code to load the current submission requirements.
      </Typography>
    );
  }

  const requirementLines = [];

  if (reasonCodeRequiresReference(reasonCode)) {
    requirementLines.push('Reference type and reference ID are required for this reason code.');
  }

  if (reasonCodeRequiresAttachment(reasonCode)) {
    requirementLines.push('At least one attachment is required for this reason code.');
  }

  if (!requirementLines.length) {
    requirementLines.push('This reason code has no additional required reference or attachment rule.');
  }

  return (
    <Alert severity="info" variant="outlined">
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">{reasonCode.label || reasonCode.reason_code}</Typography>
        {requirementLines.map((line) => (
          <Typography key={line} variant="body2">
            {line}
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
}

ReasonCodeHelper.propTypes = {
  reasonCode: PropTypes.shape({
    label: PropTypes.string,
    reason_code: PropTypes.string,
    requires_attachment: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    requires_reference: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export default function AdjustmentsCreateRequestPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState('ledger_adjustment');
  const [reasonState, setReasonState] = useState({
    loading: false,
    error: null,
    items: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [successState, setSuccessState] = useState(null);
  const [ledgerForm, setLedgerForm] = useState(LEDGER_FORM_DEFAULTS);
  const [snapshotForm, setSnapshotForm] = useState(SNAPSHOT_FORM_DEFAULTS);
  const [ledgerFiles, setLedgerFiles] = useState([]);
  const [snapshotFiles, setSnapshotFiles] = useState([]);
  const [ledgerErrors, setLedgerErrors] = useState({});
  const [snapshotErrors, setSnapshotErrors] = useState({});

  useEffect(() => {
    let active = true;

    const loadReasonCodes = async () => {
      setReasonState({
        loading: true,
        error: null,
        items: [],
      });

      try {
        const response = await fetchAdmin('/adjustments/reason_codes');

        if (!active) {
          return;
        }

        setReasonState({
          loading: false,
          error: null,
          items: (response?.reason_codes || []).filter(isReasonCodeActive),
        });
      } catch (error) {
        if (!active) {
          return;
        }

        setReasonState({
          loading: false,
          error: toErrorMessage(error),
          items: [],
        });
      }
    };

    loadReasonCodes();

    return () => {
      active = false;
    };
  }, []);

  const selectedLedgerReason = useMemo(
    () => reasonState.items.find((item) => item.reason_code === ledgerForm.reason_code) || null,
    [ledgerForm.reason_code, reasonState.items]
  );

  const selectedSnapshotReason = useMemo(
    () => reasonState.items.find((item) => item.reason_code === snapshotForm.reason_code) || null,
    [reasonState.items, snapshotForm.reason_code]
  );

  const validateFiles = (files) => {
    const invalidFile = files.find(
      (file) => file.type && !ALLOWED_FILE_TYPES.includes(file.type)
    );

    if (invalidFile) {
      return `Unsupported file type for ${invalidFile.name}.`;
    }

    return '';
  };

  const validateLedgerForm = () => {
    const nextErrors = {};
    const fileError = validateFiles(ledgerFiles);

    if (!ledgerForm.user_id.trim()) {
      nextErrors.user_id = 'User ID is required.';
    }

    if (!ledgerForm.amount.trim()) {
      nextErrors.amount = 'Amount is required.';
    } else if (Number.parseFloat(ledgerForm.amount) <= 0) {
      nextErrors.amount = 'Amount must be greater than 0.';
    }

    if (!ledgerForm.reason_code) {
      nextErrors.reason_code = 'Reason code is required.';
    }

    if (selectedLedgerReason && reasonCodeRequiresReference(selectedLedgerReason)) {
      if (!ledgerForm.reference_type.trim()) {
        nextErrors.reference_type = 'Reference type is required for this reason code.';
      }

      if (!ledgerForm.reference_id.trim()) {
        nextErrors.reference_id = 'Reference ID is required for this reason code.';
      }
    }

    if (selectedLedgerReason && reasonCodeRequiresAttachment(selectedLedgerReason) && !ledgerFiles.length) {
      nextErrors.attachments = 'At least one attachment is required for this reason code.';
    }

    if (fileError) {
      nextErrors.attachments = fileError;
    }

    setLedgerErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateSnapshotForm = () => {
    const nextErrors = {};
    const fileError = validateFiles(snapshotFiles);

    if (!snapshotForm.user_id.trim()) {
      nextErrors.user_id = 'User ID is required.';
    }

    ['balance', 'ledger_balance', 'usdt_balance', 'usdt_ledger_balance'].forEach((field) => {
      if (!snapshotForm[field].trim()) {
        nextErrors[field] = 'This value is required.';
      } else if (Number.parseFloat(snapshotForm[field]) < 0) {
        nextErrors[field] = 'This value must be 0 or greater.';
      }
    });

    if (!snapshotForm.reason_code) {
      nextErrors.reason_code = 'Reason code is required.';
    }

    if (selectedSnapshotReason && reasonCodeRequiresReference(selectedSnapshotReason)) {
      if (!snapshotForm.reference_type.trim()) {
        nextErrors.reference_type = 'Reference type is required for this reason code.';
      }

      if (!snapshotForm.reference_id.trim()) {
        nextErrors.reference_id = 'Reference ID is required for this reason code.';
      }
    }

    if (selectedSnapshotReason && reasonCodeRequiresAttachment(selectedSnapshotReason) && !snapshotFiles.length) {
      nextErrors.attachments = 'At least one attachment is required for this reason code.';
    }

    if (fileError) {
      nextErrors.attachments = fileError;
    }

    setSnapshotErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleLedgerSubmit = async () => {
    if (!validateLedgerForm()) {
      return;
    }

    setSubmitting(true);
    setSuccessState(null);

    try {
      const payload = buildCreatePayload(ledgerForm, ledgerFiles);
      const response = await postAdmin('/adjustments', payload);

      setSuccessState({
        requestId: response?.request_id || null,
        type: 'ledger_adjustment',
      });
      setLedgerForm(LEDGER_FORM_DEFAULTS);
      setLedgerFiles([]);
      setLedgerErrors({});
      enqueueSnackbar('Ledger adjustment request created successfully.', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnapshotSubmit = async () => {
    if (!validateSnapshotForm()) {
      return;
    }

    setSubmitting(true);
    setSuccessState(null);

    try {
      const payload = buildCreatePayload(snapshotForm, snapshotFiles);
      const response = await postAdmin('/adjustments/snapshot', payload);

      setSuccessState({
        requestId: response?.request_id || null,
        type: 'wallet_snapshot_correction',
      });
      setSnapshotForm(SNAPSHOT_FORM_DEFAULTS);
      setSnapshotFiles([]);
      setSnapshotErrors({});
      enqueueSnackbar('Wallet snapshot correction request created successfully.', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const currentReason = tab === 'ledger_adjustment' ? selectedLedgerReason : selectedSnapshotReason;
  const currentFiles = tab === 'ledger_adjustment' ? ledgerFiles : snapshotFiles;
  const currentFileError = tab === 'ledger_adjustment' ? ledgerErrors.attachments : snapshotErrors.attachments;

  return (
    <AdminRouteLayout title="Create Request" breadcrumbTitle="Adjustments">
      <Stack spacing={3}>
        <Stack spacing={0.75}>
          <Typography variant="h4">Create Adjustment Request</Typography>
          <Typography variant="body2" color="text.secondary">
            Submit either a ledger adjustment or a wallet snapshot correction using the current backend contract. All non-file fields are sent as strings in `multipart/form-data`.
          </Typography>
        </Stack>

        {reasonState.error ? (
          <Alert severity="error" variant="outlined">
            {reasonState.error}
          </Alert>
        ) : null}

        {successState?.requestId ? (
          <Alert
            severity="success"
            variant="outlined"
            action={
              <Button
                component={RouterLink}
                color="inherit"
                size="small"
                to={PATH_DASHBOARD.adjustments.list}
              >
                View Requests
              </Button>
            }
          >
            Created {successState.type === 'ledger_adjustment' ? 'ledger adjustment' : 'wallet snapshot correction'} request {successState.requestId}.
          </Alert>
        ) : null}

        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab value="ledger_adjustment" label="Create Ledger Adjustment" />
          <Tab value="wallet_snapshot_correction" label="Create Wallet Snapshot Correction" />
        </Tabs>

        {tab === 'ledger_adjustment' ? (
          <Stack spacing={3}>
            <AdminFormSection
              title="Adjustment Target"
              description="Capture the user and wallet location that the ledger adjustment should affect."
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Target Type"
                    value={ledgerForm.target_type}
                    onChange={(event) =>
                      setLedgerForm((prev) => ({
                        ...prev,
                        target_type: event.target.value,
                      }))
                    }
                  >
                    {TARGET_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="User ID"
                    value={ledgerForm.user_id}
                    onChange={(event) =>
                      setLedgerForm((prev) => ({
                        ...prev,
                        user_id: event.target.value,
                      }))
                    }
                    error={Boolean(ledgerErrors.user_id)}
                    helperText={ledgerErrors.user_id || 'Example: USR_123456'}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Direction"
                    value={ledgerForm.direction}
                    onChange={(event) =>
                      setLedgerForm((prev) => ({
                        ...prev,
                        direction: event.target.value,
                      }))
                    }
                  >
                    {DIRECTION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Wallet Type"
                    value={ledgerForm.wallet_type}
                    onChange={(event) =>
                      setLedgerForm((prev) => ({
                        ...prev,
                        wallet_type: event.target.value,
                        asset_code: getAssetCodeForWalletType(event.target.value),
                      }))
                    }
                  >
                    {WALLET_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Asset Code"
                    value={ledgerForm.asset_code}
                    onChange={(event) =>
                      setLedgerForm((prev) => ({
                        ...prev,
                        asset_code: event.target.value,
                      }))
                    }
                    helperText="The asset code must match the selected wallet type."
                  >
                    <MenuItem value={getAssetCodeForWalletType(ledgerForm.wallet_type)}>
                      {getAssetCodeForWalletType(ledgerForm.wallet_type)}
                    </MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </AdminFormSection>

            <AdminFormSection
              title="Financial Details"
              description="Capture a positive decimal string for the wallet balance change."
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Amount"
                    value={ledgerForm.amount}
                    onChange={(event) =>
                      setLedgerForm((prev) => ({
                        ...prev,
                        amount: normalizeDecimalInput(event.target.value),
                      }))
                    }
                    inputProps={{ inputMode: 'decimal' }}
                    error={Boolean(ledgerErrors.amount)}
                    helperText={
                      ledgerErrors.amount ||
                      `Formatted preview: ${formatDecimalForDisplay(
                        ledgerForm.amount,
                        ledgerForm.asset_code
                      )}`
                    }
                  />
                </Grid>
              </Grid>
            </AdminFormSection>

            <AdminFormSection
              title="Reason and Evidence"
              description="Reason codes are loaded from the active backend reason code endpoint and should not be hardcoded."
            >
              <Stack spacing={2.5}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Reason Code"
                      value={ledgerForm.reason_code}
                      onChange={(event) =>
                        setLedgerForm((prev) => ({
                          ...prev,
                          reason_code: event.target.value,
                        }))
                      }
                      error={Boolean(ledgerErrors.reason_code)}
                      helperText={ledgerErrors.reason_code || (reasonState.loading ? 'Loading active reason codes...' : 'Select an active backend reason code.')}
                    >
                      {reasonState.items.map((option) => (
                        <MenuItem key={option.reason_code} value={option.reason_code}>
                          {formatReasonCodeLabel(option)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Reason Note"
                      value={ledgerForm.reason_note}
                      onChange={(event) =>
                        setLedgerForm((prev) => ({
                          ...prev,
                          reason_note: event.target.value,
                        }))
                      }
                      helperText="Optional supporting note that will appear in the detail drawer."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Reference Type"
                      value={ledgerForm.reference_type}
                      onChange={(event) =>
                        setLedgerForm((prev) => ({
                          ...prev,
                          reference_type: event.target.value,
                        }))
                      }
                      error={Boolean(ledgerErrors.reference_type)}
                      helperText={ledgerErrors.reference_type || 'Optional unless the selected reason code requires it.'}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Reference ID"
                      value={ledgerForm.reference_id}
                      onChange={(event) =>
                        setLedgerForm((prev) => ({
                          ...prev,
                          reference_id: event.target.value,
                        }))
                      }
                      error={Boolean(ledgerErrors.reference_id)}
                      helperText={ledgerErrors.reference_id || 'Optional unless the selected reason code requires it.'}
                    />
                  </Grid>
                </Grid>

                <ReasonCodeHelper reasonCode={selectedLedgerReason} />

                <Stack spacing={1.5}>
                  <Button variant="outlined" component="label">
                    Select Attachments
                    <input
                      hidden
                      type="file"
                      multiple
                      accept={FILE_ACCEPT_ATTRIBUTE}
                      onChange={(event) => setLedgerFiles(sanitizeFiles(event.target.files))}
                    />
                  </Button>
                  <FileSummary files={ledgerFiles} error={ledgerErrors.attachments} />
                </Stack>
              </Stack>
            </AdminFormSection>

            <LoadingButton variant="contained" size="large" loading={submitting} onClick={handleLedgerSubmit}>
              Create Ledger Adjustment
            </LoadingButton>
          </Stack>
        ) : null}

        {tab === 'wallet_snapshot_correction' ? (
          <Stack spacing={3}>
            <AdminFormSection
              title="Adjustment Target"
              description="Capture the user and audit direction for the wallet snapshot correction."
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Target Type"
                    value={snapshotForm.target_type}
                    onChange={(event) =>
                      setSnapshotForm((prev) => ({
                        ...prev,
                        target_type: event.target.value,
                      }))
                    }
                  >
                    {TARGET_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="User ID"
                    value={snapshotForm.user_id}
                    onChange={(event) =>
                      setSnapshotForm((prev) => ({
                        ...prev,
                        user_id: event.target.value,
                      }))
                    }
                    error={Boolean(snapshotErrors.user_id)}
                    helperText={snapshotErrors.user_id || 'Example: USR_123456'}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Direction"
                    value={snapshotForm.direction}
                    onChange={(event) =>
                      setSnapshotForm((prev) => ({
                        ...prev,
                        direction: event.target.value,
                      }))
                    }
                    helperText="Direction is stored as audit context for snapshot corrections."
                  >
                    {DIRECTION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </AdminFormSection>

            <AdminFormSection
              title="Snapshot Values"
              description="Set the exact target wallet values that the backend should write."
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Balance"
                    value={snapshotForm.balance}
                    onChange={(event) =>
                      setSnapshotForm((prev) => ({
                        ...prev,
                        balance: normalizeDecimalInput(event.target.value),
                      }))
                    }
                    inputProps={{ inputMode: 'decimal' }}
                    error={Boolean(snapshotErrors.balance)}
                    helperText={snapshotErrors.balance || `Preview: ${formatDecimalForDisplay(snapshotForm.balance, 'NGN')}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Ledger Balance"
                    value={snapshotForm.ledger_balance}
                    onChange={(event) =>
                      setSnapshotForm((prev) => ({
                        ...prev,
                        ledger_balance: normalizeDecimalInput(event.target.value),
                      }))
                    }
                    inputProps={{ inputMode: 'decimal' }}
                    error={Boolean(snapshotErrors.ledger_balance)}
                    helperText={snapshotErrors.ledger_balance || `Preview: ${formatDecimalForDisplay(snapshotForm.ledger_balance, 'NGN')}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="USDT Balance"
                    value={snapshotForm.usdt_balance}
                    onChange={(event) =>
                      setSnapshotForm((prev) => ({
                        ...prev,
                        usdt_balance: normalizeDecimalInput(event.target.value),
                      }))
                    }
                    inputProps={{ inputMode: 'decimal' }}
                    error={Boolean(snapshotErrors.usdt_balance)}
                    helperText={snapshotErrors.usdt_balance || `Preview: ${formatDecimalForDisplay(snapshotForm.usdt_balance, 'USDT')}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="USDT Ledger Balance"
                    value={snapshotForm.usdt_ledger_balance}
                    onChange={(event) =>
                      setSnapshotForm((prev) => ({
                        ...prev,
                        usdt_ledger_balance: normalizeDecimalInput(event.target.value),
                      }))
                    }
                    inputProps={{ inputMode: 'decimal' }}
                    error={Boolean(snapshotErrors.usdt_ledger_balance)}
                    helperText={snapshotErrors.usdt_ledger_balance || `Preview: ${formatDecimalForDisplay(snapshotForm.usdt_ledger_balance, 'USDT')}`}
                  />
                </Grid>
              </Grid>
            </AdminFormSection>

            <AdminFormSection
              title="Reason and Evidence"
              description="Use backend-driven reason codes and attach supporting proof before submitting."
            >
              <Stack spacing={2.5}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Reason Code"
                      value={snapshotForm.reason_code}
                      onChange={(event) =>
                        setSnapshotForm((prev) => ({
                          ...prev,
                          reason_code: event.target.value,
                        }))
                      }
                      error={Boolean(snapshotErrors.reason_code)}
                      helperText={snapshotErrors.reason_code || (reasonState.loading ? 'Loading active reason codes...' : 'Select an active backend reason code.')}
                    >
                      {reasonState.items.map((option) => (
                        <MenuItem key={option.reason_code} value={option.reason_code}>
                          {formatReasonCodeLabel(option)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Reason Note"
                      value={snapshotForm.reason_note}
                      onChange={(event) =>
                        setSnapshotForm((prev) => ({
                          ...prev,
                          reason_note: event.target.value,
                        }))
                      }
                      helperText="Optional supporting note that will appear in the detail drawer."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Reference Type"
                      value={snapshotForm.reference_type}
                      onChange={(event) =>
                        setSnapshotForm((prev) => ({
                          ...prev,
                          reference_type: event.target.value,
                        }))
                      }
                      error={Boolean(snapshotErrors.reference_type)}
                      helperText={snapshotErrors.reference_type || 'Optional unless the selected reason code requires it.'}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Reference ID"
                      value={snapshotForm.reference_id}
                      onChange={(event) =>
                        setSnapshotForm((prev) => ({
                          ...prev,
                          reference_id: event.target.value,
                        }))
                      }
                      error={Boolean(snapshotErrors.reference_id)}
                      helperText={snapshotErrors.reference_id || 'Optional unless the selected reason code requires it.'}
                    />
                  </Grid>
                </Grid>

                <ReasonCodeHelper reasonCode={selectedSnapshotReason} />

                <Stack spacing={1.5}>
                  <Button variant="outlined" component="label">
                    Select Attachments
                    <input
                      hidden
                      type="file"
                      multiple
                      accept={FILE_ACCEPT_ATTRIBUTE}
                      onChange={(event) => setSnapshotFiles(sanitizeFiles(event.target.files))}
                    />
                  </Button>
                  <FileSummary files={snapshotFiles} error={snapshotErrors.attachments} />
                </Stack>
              </Stack>
            </AdminFormSection>

            <LoadingButton variant="contained" size="large" loading={submitting} onClick={handleSnapshotSubmit}>
              Create Wallet Snapshot Correction
            </LoadingButton>
          </Stack>
        ) : null}

        <AdminFormSection
          title="Current Upload State"
          description="Review the selected file set before submitting the current request type."
        >
          <FileSummary files={currentFiles} error={currentFileError} />
          <ReasonCodeHelper reasonCode={currentReason} />
        </AdminFormSection>
      </Stack>
    </AdminRouteLayout>
  );
}
